/**
 * Transform cameras from API response to gallery photo format.
 * This is the single point of change when the backend modifies the response format.
 */

export interface ApiCameraEntry {
    imageName: string;
    cameraId: string;
    imageUrl: string;
    colmapCameraId: number;
    colmapImageId: number;
    rotation: { x: number; y: number; z: number; w: number };
    position: { x: number; y: number; z: number };
}

export interface ApiPhotoEntry {
    path: string;
    frameName: string;
    subtype: string;
    frameNum: number;
    type: string;
    key: string;
    sizeBytes?: number;
    url: string;
}

export interface GalleryPhoto {
    id: string;
    url: string;
    cameraId: string;
    name: string;
}

export const CAMERA_THRESHOLD = 100;

export function isModelFailed(cameraCount: number): boolean {
    return cameraCount < CAMERA_THRESHOLD;
}

export interface FallbackDamage {
    id: string;
    name: string;
    description: string;
    selectValue: string;
    photoId: string;
    cameraId: string;
    markerX: number;
    markerY: number;
}

export function transformCamerasToGalleryPhotos(
    cameras: ApiCameraEntry[]
): GalleryPhoto[] {
    return cameras.map((camera, index) => ({
        id: camera.cameraId,
        url: camera.imageUrl,
        cameraId: camera.cameraId,
        name: camera.imageName || `Cámara ${index + 1}`
    }));
}

export function transformPhotosToGalleryPhotos(
    photos: ApiPhotoEntry[]
): GalleryPhoto[] {
    return photos.map((photo, index) => ({
        id: `photo-${photo.frameNum ?? index}`,
        url: photo.url,
        cameraId: `photo-${photo.frameNum ?? index}`,
        name: photo.frameName || `Foto ${index + 1}`
    }));
}

export function calculateRelativeCoordinates(
    clickX: number,
    clickY: number,
    width: number,
    height: number
): { markerX: number; markerY: number } {
    return {
        markerX: clickX / width,
        markerY: clickY / height
    };
}

export function filterDamagesByPhoto(
    damages: FallbackDamage[],
    photoId: string
): FallbackDamage[] {
    return damages.filter(d => d.photoId === photoId);
}

export interface FallbackDamageApiPayload {
    id: string;
    name: string;
    description: string;
    selectValue: string;
    photoIds: string[];
    position: {
        x: string;
        y: string;
        z: string;
    };
    cameraId: string;
    cameraIds: string[];
    markerX: string;
    markerY: string;
    source: string;
}

export function transformFallbackDamageToApiPayload(
    damage: FallbackDamage
): FallbackDamageApiPayload {
    return {
        id: damage.id,
        name: damage.name,
        description: damage.description,
        selectValue: damage.selectValue,
        photoIds: [damage.photoId],
        position: {
            x: damage.markerX.toString(),
            y: damage.markerY.toString(),
            z: "0"
        },
        cameraId: damage.cameraId,
        cameraIds: [damage.cameraId],
        markerX: damage.markerX.toString(),
        markerY: damage.markerY.toString(),
        source: "fallback-gallery"
    };
}
